/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';
import { useEffect, useState } from 'react';
import Image from 'next/image';
import { Avatar, Button, Dropdown, MenuProps, Modal, Space } from 'antd';
import {
    SearchOutlined,
    CalculatorOutlined,
    GlobalOutlined,
    SafetyCertificateOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
    RocketOutlined,
    CheckCircleOutlined,
    MenuOutlined,
    CloseOutlined,
    UserOutlined,
    LogoutOutlined,
    PoweroffOutlined,
    DollarOutlined,
    FileSearchOutlined,
    BookOutlined,
    CompassOutlined,
    BarChartOutlined,
    FacebookOutlined,
    LockOutlined
} from '@ant-design/icons';
import Link from 'next/link';
import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';
import FotterComponent from '../components/FotterComponent';

export default function HomeComponent() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeApp, setActiveApp] = useState<'tariff' | 'cf'>('tariff');
    const [isLogin, setIsLogin] = useState(false)
    const router = useRouter();
    const [modal, contextHolder] = Modal.useModal();
    const [productList, setProductList] = useState<any[]>([])

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const token = Cookies.get("token") || localStorage.getItem("token");
            if (token) {
                const getProducts = Cookies.get("products") || localStorage.getItem("products");
                let productsArray = [];
                try {
                    productsArray = JSON.parse(getProducts || "[]");
                } catch (e) {
                    console.error("Invalid products format", e);
                }
                setProductList(productsArray);
                setIsLogin(true);
            } else {
                setIsLogin(false);
            }
        }
    }, []);

    const items: MenuProps["items"] = [
        ...(productList.length > 0 && productList[0]?.id === 1
            ? [
                {
                    key: "customTariff",
                    label: "Custom Tariff",
                    icon: <CalculatorOutlined />,
                    danger: false,
                    onClick: () => router.push("/dashboard"),
                },
            ]
            : []),
        ...(productList.length > 0 && productList[0]?.id === 2
            ? [
                {
                    key: "cfAgents",
                    label: "C&F Agents",
                    icon: <CalculatorOutlined />,
                    danger: false,
                    onClick: () => router.push("/cfJobSolution"),
                },
            ]
            : []),
        {
            key: "ForgetPassword",
            label: "Forget Password",
            icon: <LockOutlined />,
            danger: false,
            onClick: () => router.push("/auth/forget-password"),
        },
        {
            key: "logout",
            label: "Logout",
            icon: <LogoutOutlined />,
            danger: true,
            onClick: () => handleLogout()
        },
    ];

    const handleLogout = () => {
        document.cookie.split(";").forEach((cookie) => {
            const name = cookie.split("=")[0].trim();
            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
        });
        localStorage.clear();
        sessionStorage.clear();
        router.replace("/");
        window.location.reload();
    };

    // Feature data
    const features = [
        { icon: SearchOutlined, color: 'from-blue-500 to-blue-600', title: 'HS Code Search', desc: 'Fast & precise HS code search for accurate product classification.' },
        { icon: DollarOutlined, color: 'from-teal-500 to-teal-600', title: 'Duty & Tax Calculator', desc: 'Calculate CD, RD, SD, VAT, AIT, AT and total applicable duties.' },
        { icon: BookOutlined, color: 'from-purple-500 to-purple-600', title: 'SROs & Policies', desc: 'Access Import/Export Policy Orders, SROs, Customs Act & Rules.' },
        { icon: CompassOutlined, color: 'from-indigo-500 to-indigo-600', title: 'Complete Product View', desc: 'View tariff, SROs, policies and assessment rulings in one place.' },
        { icon: GlobalOutlined, color: 'from-cyan-500 to-cyan-600', title: 'Customs & Port Info', desc: 'Find customs stations, CPC codes and port information.' },
        { icon: BarChartOutlined, color: 'from-rose-500 to-rose-600', title: 'Reports & Updates', desc: 'Fast search, downloadable reports and regular online updates.' },
    ];

    // Audience data
    const audience = [
        { icon: '🚢', title: 'Importers & Exporters', desc: 'Make faster, informed trade decisions.' },
        { icon: '📦', title: 'C&F Agents & Freight Forwarders', desc: 'Speed up classification and documentation.' },
        { icon: '📊', title: 'Customs Consultants & Trade Advisors', desc: 'Research tariffs, regulations and SROs efficiently.' },
        { icon: '🏦', title: 'Banks & Financial Institutions', desc: 'Cross-reference trade, finance and L/C information.' },
        { icon: '🔗', title: 'Supply Chain & Businesses', desc: 'Support procurement, costing and logistics.' },
    ];

    // Trust stats
    const trustStats = [
        { icon: '⭐', stat: '25+ YEARS', label: 'Customs & Trade Experience' },
        { icon: '📋', stat: 'FY 2026–2027', label: 'Latest Budget & Tariff Compliant' },
        { icon: '🎯', stat: 'ALL-IN-ONE', label: 'Complete Customs Reference Platform' },
        { icon: '🌐', stat: '24/7 ACCESS', label: 'Anywhere, Anytime · Any Device' },
    ];

    // Tool cards data
    const toolCards = [
        { icon: '⌕', title: 'Find accurate HS Code', desc: 'Quick search for precise product classification.' },
        { icon: '◇', title: 'VIEW ALL-IN-ONE PLACE', desc: 'View Product details, tariff, SROs, policies and assessment rulings in one place.' },
        { icon: '▦', title: 'Calculate provisional import duty and taxes', desc: 'CD, RD, SD, VAT, AIT, AT and other applicable duties.' },
        { icon: '◉', title: 'Estimate import costs', desc: 'Calculate landed cost and unit cost with full duty breakdown.' },
        { icon: '▤', title: 'Access SROs & Policies', desc: 'Import/Export Policy Orders, SROs, Customs Act, Rules & more.' },
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Header - Navy Blue like the design */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-[#06264f] shadow-lg h-[70px]">
                <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link href="/">
                            <div className="flex items-center gap-2.5">
                                <Image
                                    src="/logo192px.png"
                                    alt="Customs Solution Logo"
                                    width={42}
                                    height={42}
                                    className="rounded-lg"
                                />
                                <div className="text-white font-extrabold text-[19px] leading-tight hidden sm:block">
                                    CUSTOMS SOLUTION
                                    <span className="block text-xs font-medium text-gray-300">
                                        Simplifying Global Customs Clearance
                                    </span>
                                </div>
                            </div>
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-7 text-sm">
                        {/* Facebook Link */}
                        <a
                            href="https://www.facebook.com/profile.php?id=61593161902812"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                            aria-label="Visit Customs Solution on Facebook"
                        >
                            <Image
                                src="/facebook-logo.png"
                                alt="Facebook"
                                width={36}
                                height={36}
                                className="rounded-full"
                            />
                        </a>
                        <a href="#home" className="text-white hover:text-teal-300 font-medium transition-colors">
                            Home
                        </a>
                        <a href="#features" className="text-white hover:text-teal-300 font-medium transition-colors">
                            Features
                        </a>
                        <a href="#pricing" className="text-white hover:text-teal-300 font-medium transition-colors">
                            Pricing
                        </a>
                        <a href="#audience" className="text-white hover:text-teal-300 font-medium transition-colors">
                            Users
                        </a>
                        <a href="#contact" className="text-white hover:text-teal-300 font-medium transition-colors">
                            Contact
                        </a>

                        {isLogin ? (
                            <Dropdown
                                menu={{ items }}
                                placement="bottomRight"
                                trigger={["click"]}
                                arrow
                            >
                                <Space style={{ cursor: "pointer" }}>
                                    <Avatar icon={<UserOutlined />} style={{ backgroundColor: "#08b5aa" }} />
                                </Space>
                            </Dropdown>
                        ) : (
                            <div className="flex items-center gap-2.5">
                                <Link href="/auth/login">
                                    <Button
                                        className="border-white/40 text-white hover:bg-white/10 font-semibold text-sm h-auto py-2.5 px-4"
                                        style={{ background: '#fff', color: '#06264f' }}
                                    >
                                        Sign In
                                    </Button>
                                </Link>
                                <Link href="/auth/register">
                                    <Button
                                        type="primary"
                                        className="bg-teal-500 hover:bg-teal-600 font-semibold text-sm h-auto py-2.5 px-4 border-0"
                                        style={{ background: '#08b5aa' }}
                                    >
                                        Subscribe
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </nav>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="md:hidden text-white p-2"
                        aria-label="Toggle menu"
                    >
                        {mobileMenuOpen ? <CloseOutlined className="text-2xl" /> : <MenuOutlined className="text-2xl" />}
                    </button>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden bg-[#06264f] border-t border-white/10">
                        <nav className="px-4 py-6 space-y-4">
                            {[
                                { href: '#home', label: 'Home' },
                                { href: '#features', label: 'Features' },
                                { href: '#pricing', label: 'Pricing' },
                                { href: '#audience', label: 'Users' },
                                { href: '#contact', label: 'Contact' },
                            ].map((item) => (
                                <a
                                    key={item.href}
                                    href={item.href}
                                    className="block text-white text-lg font-medium hover:text-teal-300 transition-colors py-2"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {item.label}
                                </a>
                            ))}
                            <div>
                                {isLogin && (
                                    <Link href="/dashboard" className="text-white hover:text-teal-300 font-medium transition-colors py-2 inline-block">
                                        Customs Tariff
                                    </Link>
                                )}
                                <br />
                                {isLogin && (
                                    <Link
                                        href="/auth/forget-password"
                                        className="text-white hover:text-teal-300 font-medium transition-colors inline-block mt-4"
                                    >
                                        Forget Password
                                    </Link>
                                )}
                            </div>
                      
                            <div className="pt-4 space-y-3 border-t border-white/10">
                                {isLogin ? (
                                    <Button
                                        danger
                                        size="large"
                                        block
                                        onClick={handleLogout}
                                    >
                                        Logout
                                    </Button>
                                ) : (
                                    <>
                                        <Link href="/auth/login" onClick={() => setMobileMenuOpen(false)}>
                                            <Button size="large" block className="border-white/40 text-white font-semibold mb-2" style={{ background: '#fff' }}>
                                                Sign In
                                            </Button>
                                        </Link>
                                        <Link href="/auth/register" onClick={() => setMobileMenuOpen(false)}>
                                            <Button
                                                type="primary"
                                                size="large"
                                                block
                                                className="bg-teal-500 hover:bg-teal-600 font-semibold border-0"
                                                style={{ background: '#08b5aa' }}
                                            >
                                                Subscribe
                                            </Button>
                                        </Link>
                                    </>
                                )}
                            </div>
                        </nav>
                    </div>
                )}
            </header>

            {/* Hero Section - Matching Original Design */}
            <section id="home"
                className="relative overflow-hidden min-h-[470px] pt-[70px]"
                style={{
                    background: 'linear-gradient(90deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 31%, rgba(255,255,255,0.20) 61%, rgba(255,255,255,0) 100%), url("/customs-hero.jpg") center/cover no-repeat',
                }}
            >
                <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-12 gap-4 items-center  pt-[50px]">
                        {/* Left Content - 51% width */}
                        <div className="lg:col-span-8 col-span-12">
                        <div className="w-full  py-14 space-y-4">
                            <div className="text-teal-600 font-extrabold text-[13px] uppercase tracking-[1px]">
                                FY 2026–2027 Updated
                            </div>
                            <h1 className="text-[43px] sm:text-5xl lg:text-[66px] font-extrabold text-[#06264f] leading-[1.02] tracking-[-2.5px]">
                                Simplifying Global Customs <span className="text-teal-500">Clearance</span>
                            </h1>
                            <p className="text-[17px] text-[#44536a] max-w-[570px]">
                                Customs Solution helps importers, exporters, C&amp;F agents and trade professionals search HS Codes, calculate provisional customs duties and taxes, estimate import costs, and access SROs, customs policies and regulatory information — all in one powerful, web-based platform.
                            </p>
                            <div className="flex flex-wrap gap-3.5 pt-1">
                                <Link href="/auth/register">
                                    <Button
                                        type="primary"
                                        size="large"
                                        className="bg-teal-500 hover:bg-[#079d96] border-0 font-bold text-sm h-[48px] px-5"
                                        style={{ background: '#08b5aa' }}
                                    >
                                        Subscribe Now →
                                    </Button>
                                </Link>
                                <a href="#features">
                                    <Button
                                        size="large"
                                        className="border-[#ccd7e5] text-[#06264f] font-bold text-sm h-[48px] px-5 hover:bg-gray-50"
                                    >
                                        Explore Features →
                                    </Button>
                                </a>
                            </div>
                            <div className="flex flex-wrap gap-5 text-xs font-bold text-[#526177]">
                                <span className="flex items-center gap-1.5">
                                    <span className="text-green-600 font-black text-sm">✓</span> FY 2026–2027 Updated
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="text-green-600 font-black text-sm">✓</span> Web-Based
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="text-green-600 font-black text-sm">✓</span> Access from any device
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="text-green-600 font-black text-sm">✓</span> Always up to date
                                </span>
                            </div>
                        </div>
                        </div>
                        <div className="lg:col-span-4 col-span-12">
                        {/* Tool Cards - Positioned Absolutely on the Right */}
                        <div className="hidden lg:block right-[max(25px,calc((100vw-1180px)/2+16px))] top-[90px] w-[270px] space-y-[12px] pb-10">
                            {toolCards.map((tool, index) => (
                                <div
                                    key={index}
                                    className="bg-white/94 backdrop-blur-[9px] border border-white/80 rounded-[13px] p-[13px] flex gap-3 shadow-[0_9px_25px_rgba(7,38,79,0.12)] transition-all duration-300 hover:-translate-y-1 hover:scale-[1.025] hover:border-teal-300/40 hover:shadow-[0_16px_35px_rgba(7,38,79,0.19)] group relative overflow-hidden"
                                    style={{
                                        opacity: 0,
                                        transform: 'translateX(35px)',
                                        animation: `heroToolEnter 1.2s ease-out forwards ${0.35 * index}s`
                                    }}
                                >
                                    {/* Shimmer effect */}
                                    <div className="absolute inset-0 -translate-x-[130%] w-[65%] h-full bg-gradient-to-r from-transparent via-white/65 to-transparent skew-x-[-20deg] group-hover:translate-x-[240%] transition-all duration-700 pointer-events-none"></div>

                                    <div className="w-[39px] h-[39px] rounded-[10px] flex-shrink-0 bg-[#e9f8ff] text-[#0878e8] text-[19px] font-black flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3 group-hover:bg-gradient-to-br group-hover:from-teal-500 group-hover:to-blue-500 group-hover:text-white group-hover:shadow-[0_5px_14px_rgba(8,120,232,0.20)]">
                                        {tool.icon}
                                    </div>
                                    <div>
                                        <strong className="text-[#06264f] text-base block transition-all duration-300 group-hover:text-teal-500 group-hover:translate-x-0.5">
                                            {tool.title}
                                        </strong>
                                        <p className="text-xs text-[#026a74] leading-tight mt-0.5 transition-all duration-300 group-hover:text-[#44536a] group-hover:translate-x-0.5">
                                            {tool.desc}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Mobile Tool Cards */}
                        <div className="lg:hidden relative w-full space-y-[11px] pb-4">
                            {toolCards.map((tool, index) => (
                                <div
                                    key={index}
                                    className="bg-white/94 backdrop-blur-[9px] border border-white/80 rounded-[13px] p-[13px] flex gap-3 shadow-[0_9px_25px_rgba(7,38,79,0.12)] transition-all duration-300 hover:-translate-y-1 hover:scale-[1.025] hover:border-teal-300/40 hover:shadow-[0_16px_35px_rgba(7,38,79,0.19)] group relative overflow-hidden"
                                    style={{
                                        opacity: 0,
                                        transform: 'translateY(20px)',
                                        animation: `heroToolEnterMobile 1.2s ease-out forwards ${0.35 * index}s`
                                    }}
                                >
                                    <div className="absolute inset-0 -translate-x-[130%] w-[65%] h-full bg-gradient-to-r from-transparent via-white/65 to-transparent skew-x-[-20deg] group-hover:translate-x-[240%] transition-all duration-700 pointer-events-none"></div>

                                    <div className="w-[39px] h-[39px] rounded-[10px] flex-shrink-0 bg-[#e9f8ff] text-[#0878e8] text-[19px] font-black flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3 group-hover:bg-gradient-to-br group-hover:from-teal-500 group-hover:to-blue-500 group-hover:text-white group-hover:shadow-[0_5px_14px_rgba(8,120,232,0.20)]">
                                        {tool.icon}
                                    </div>
                                    <div>
                                        <strong className="text-[#06264f] text-base block transition-all duration-300 group-hover:text-teal-500 group-hover:translate-x-0.5">
                                            {tool.title}
                                        </strong>
                                        <p className="text-xs text-[#026a74] leading-tight mt-0.5 transition-all duration-300 group-hover:text-[#44536a] group-hover:translate-x-0.5">
                                            {tool.desc}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trust Section */}
            <section className="bg-[#06264f] text-white lg:py-[70px]">
                <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 sm:grid-cols-4">
                        {trustStats.map((item, i) => (
                            <div
                                key={i}
                                className={`p-[19px] flex gap-3.5 items-center ${i < 3 ? 'border-r border-white/10' : ''} border-b border-white/10 sm:border-b-0`}
                            >
                                <span className="text-2xl text-yellow-400">{item.icon}</span>
                                <div>
                                    <strong className="block text-teal-300 text-base">{item.stat}</strong>
                                    <span className="text-sm text-[#c8d5e6]">{item.label}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-[65px] px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#f0f7ff] to-white">
                <div className="max-w-[1180px] mx-auto">
                    <div className="text-center max-w-[720px] mx-auto mb-[42px]">
                        <h2 className="text-[32px] font-extrabold text-[#06264f] tracking-[-0.8px]">
                            Customs Solution Features
                        </h2>
                        <div className="w-10 h-1 bg-teal-500 rounded-full mx-auto mt-2.5"></div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-[18px]">
                        {features.map((feature, i) => (
                            <div
                                key={i}
                                className="relative overflow-hidden min-h-[210px] bg-[linear-gradient(145deg,#ffffff_0%,#a9cff5_100%)] border border-[#e3ebf3] rounded-[18px] p-[27px] shadow-[0_5px_18px_rgba(6,38,79,0.045)] transition-all duration-300 hover:-translate-y-2 hover:border-teal-300/35 hover:shadow-[0_18px_40px_rgba(6,38,79,0.11)] group"
                            >
                                {/* Decorative glow */}
                                <div className="absolute -right-11 -top-11 w-[100px] h-[100px] rounded-full bg-teal-500/10 transition-all duration-300 group-hover:scale-150"></div>

                                <div className={`w-[52px] h-[52px] rounded-[14px] flex items-center justify-center mb-[18px] bg-gradient-to-br ${feature.color} text-white text-3xl shadow-[inset_0_0_0_1px_rgba(8,120,232,0.08)] transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3`}>
                                    <feature.icon />
                                </div>
                                <h3 className="text-lg font-bold text-[#06264f] mb-2">{feature.title}</h3>
                                <p className="text-sm text-gray-600 leading-relaxed max-w-[270px]">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Audience Section */}
            <section id="audience" className="py-[65px] px-4 sm:px-6 lg:px-8 relative overflow-hidden"
                style={{
                    background: 'linear-gradient(135deg, #f3f9fd 0%, #a5d0ec 50%, #a3f0e3 100%)'
                }}
            >
                <div className="absolute -top-[220px] -right-[150px] w-[420px] h-[420px] rounded-full bg-teal-500/10"></div>
                <div className="absolute -bottom-[170px] -left-[120px] w-[300px] h-[300px] rounded-full bg-blue-500/10"></div>

                <div className="max-w-[1180px] mx-auto relative z-10">
                    <div className="text-center max-w-[720px] mx-auto mb-[38px]">
                        <h2 className="text-[32px] font-extrabold text-[#06264f] tracking-[-0.8px]">
                            Who Should Use Customs Solution?
                        </h2>
                        <div className="w-10 h-1 bg-teal-500 rounded-full mx-auto mt-2.5"></div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-[14px]">
                        {audience.map((item, i) => (
                            <div
                                key={i}
                                className="bg-white/90 border border-[#dce7f1] rounded-[17px] p-[25px] text-center min-h-[190px] shadow-[0_8px_25px_rgba(6,38,79,0.05)] transition-all duration-300 hover:-translate-y-2 hover:bg-white hover:shadow-[0_18px_35px_rgba(6,38,79,0.11)]"
                            >
                                <div className="w-[52px] h-[52px] rounded-full flex items-center justify-center mx-auto mb-[15px] bg-gradient-to-br from-teal-50 to-blue-50 text-3xl transition-all duration-300 hover:bg-gradient-to-br hover:from-teal-500 hover:to-blue-500 hover:text-white hover:scale-110">
                                    {item.icon}
                                </div>
                                <h3 className="text-base font-bold text-[#06264f] leading-tight">{item.title}</h3>
                                <p className="text-sm text-gray-600 mt-2.5 leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-[65px] px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
                <div className="max-w-[1180px] mx-auto">
                    <div className="text-center max-w-[720px] mx-auto mb-[35px]">
                        <h2 className="text-[32px] font-extrabold text-[#06264f] tracking-[-0.8px]">
                            Customs Solution Subscription
                        </h2>
                        <div className="w-10 h-1 bg-teal-500 rounded-full mx-auto mt-2.5"></div>
                        <p className="text-sm text-gray-600 mt-3 max-w-[600px] mx-auto">
                            Get complete access to Customs Solution and keep your customs information up to date throughout the year.
                        </p>
                    </div>

                    <div className="flex justify-center">
                        <div className="relative w-full max-w-[430px] p-[35px] bg-gradient-to-br from-white to-[#f8fbfd] border border-[#dce7f0] rounded-[22px] shadow-[0_15px_45px_rgba(6,38,79,0.10)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_22px_55px_rgba(6,38,79,0.15)]">
                            {/* Top gradient strip */}
                            <div className="absolute top-0 left-0 right-0 h-[6px] bg-gradient-to-r from-teal-500 to-blue-500 rounded-t-[22px]"></div>
                            {/* Decorative circle */}
                            <div className="absolute -right-20 top-24 w-[180px] h-[180px] rounded-full bg-teal-500/5 pointer-events-none"></div>

                            <div className="text-center relative z-10">
                                <h3 className="text-[21px] font-extrabold text-[#06264f]">Customs Solution</h3>
                            </div>

                            <div className="text-center relative z-10 my-[23px]">
                                <span className="text-[32px] font-extrabold text-teal-500 tracking-[-1px]">
                                    <span className="text-[17px] text-[#06264f] font-bold align-middle mr-1">BDT</span> 10,000
                                    <span className="text-base text-[#718095] font-semibold ml-1">/ 1st FY</span>
                                </span>
                            </div>

                            <div className="text-center text-sm text-[#68778b] relative z-10 mb-[25px]">
                                Per User
                            </div>

                            <ul className="border-t border-[#e5ebf1] pt-5 space-y-[13px] relative z-10">
                                {['Full Feature Access', 'Regular Online Updates', '24/7 Online Customer Service', 'Discounted subscription rate from the next FY'].map((item, i) => (
                                    <li key={i} className="text-[16px] text-[#526177] pl-[27px] relative leading-relaxed">
                                        <span className="absolute left-0 top-0 w-[19px] h-[19px] rounded-full bg-[#e3f8f5] text-teal-600 text-sm font-black flex items-center justify-center">✓</span>
                                        {item}
                                    </li>
                                ))}
                            </ul>

                            <Link href="/auth/register">
                                <Button
                                    type="primary"
                                    size="large"
                                    block
                                    className="mt-[27px] bg-gradient-to-r from-teal-500 to-blue-500 border-0 font-semibold text-base h-[52px] shadow-[0_8px_20px_rgba(8,120,232,0.18)] hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(8,120,232,0.27)] transition-all duration-300"
                                >
                                    Subscribe Now →
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="px-4 sm:px-6 lg:px-8">
                <div className="max-w-[1180px] mx-auto">
                    <div className="bg-[#06264f] text-white p-[35px] sm:p-10 rounded-t-[17px] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                        <div>
                            <h2 className="text-[27px] font-bold">Ready to Simplify Your Customs Workflow?</h2>
                            <p className="text-[13px] text-[#c5d3e4] mt-1">Get faster access to customs data, tariffs, regulations and duty calculations — all in one place.</p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Link href="/auth/register">
                                <Button type="primary" size="large" className="bg-teal-500 hover:bg-[#079d96] border-0 font-semibold">
                                    Get started →
                                </Button>
                            </Link>
                            <Link href="/auth/login">
                                <Button size="large" className="bg-transparent border-white/40 text-white hover:bg-white/10 font-semibold">
                                    Sign In →
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <FotterComponent />
        </div>
    );
}